import { forwardRef } from "react";
import type { InputHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useFieldControl } from "./Field";
import "./Input.css";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Forces the invalid state. When the input sits inside a `Field` that has an
   * `error`, this is inferred and can be left alone.
   */
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, className, id, type = "text", ...rest },
  ref,
) {
  const field = useFieldControl();
  const isInvalid = invalid ?? field?.["aria-invalid"] === true;
  const describedBy = rest["aria-describedby"] ?? field?.["aria-describedby"];

  const classes = ["fc-input", isInvalid ? "is-invalid" : null, className]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return (
    <input
      {...rest}
      ref={ref}
      type={type}
      id={id ?? field?.id}
      className={classes}
      aria-describedby={describedBy}
      aria-invalid={isInvalid || undefined}
    />
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ invalid, className, id, rows = 4, ...rest }, ref) {
    const field = useFieldControl();
    const isInvalid = invalid ?? field?.["aria-invalid"] === true;
    const describedBy = rest["aria-describedby"] ?? field?.["aria-describedby"];

    const classes = [
      "fc-input",
      "fc-input--textarea",
      isInvalid ? "is-invalid" : null,
      className,
    ]
      .filter((value): value is string => Boolean(value))
      .join(" ");

    return (
      <textarea
        {...rest}
        ref={ref}
        rows={rows}
        id={id ?? field?.id}
        className={classes}
        aria-describedby={describedBy}
        aria-invalid={isInvalid || undefined}
      />
    );
  },
);
